import type { AckPayloads, AudienceInput, MakerAgent } from '@ralph/contracts'
import { ACKS } from '@ralph/contracts'
import { CircleCheck, Clock, TriangleAlert, Users } from 'lucide-react'
import { useId, useState } from 'react'
import { ApiRequestError } from '@/api/client.ts'
import type { PublishOutcome } from '@/api/maker.ts'
import { ACK_MAX } from '@/components/marketplace/maker/maker-limits.ts'
import {
  type PublishPlan,
  type PublishWording,
  publishOutcomeWording
} from '@/components/marketplace/maker/publish-wording.ts'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { usePublishMakerAgent, useRestoreMakerAgent } from '@/hooks/use-maker.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'

/**
 * The consent step, for publishing and for bringing a retired agent back (PRD-26 D-40, D-50).
 *
 * It says three things, in order, all computed by the builder from the choice being made: what
 * happens **now**, what happens **if an administrator approves** (only when something waits), and
 * **what they get** — the consent sentence, including the bases that will not reach some of them.
 * Those sentences are sent back verbatim as `ackText`, so the record holds what the human was
 * actually told (AGM-042). The checkbox starts unchecked and the contract types `ack` as a literal
 * `true` — there is no shape in which a pre-checked box could be submitted.
 *
 * Since PRD-27 D-7 the text is the EVIDENCE and not the whole record. Beside it goes the key of
 * the catalogue sentence that produced it, the version of that sentence, the language it was read
 * in, and the values it interpolated — so the same acknowledgement can be read back in either
 * language and checked against the evidence. The dialog is the only place that knows which entry
 * it rendered, which is why it is the thing that sends the key.
 *
 * The outcome is read from what the server did (`published`, `request`), not from the prediction:
 * a manager removed a moment ago is judged by the server as they stand then.
 *
 * Two structural rules the browser suite depends on. There is exactly ONE checkbox in here, and
 * the `<h2>` changes its TEXT when the publication resolves rather than being replaced by a
 * different heading, because the dialog is labelled by it.
 */
export const PublishDialog = ({
  agent,
  mode,
  audience,
  plan,
  wording,
  audiencePhrase,
  statements,
  consent,
  ackParams,
  who,
  personal,
  blocked,
  measuring,
  onClose,
  onDone
}: {
  agent: MakerAgent
  mode: 'publish' | 'restore'
  audience: AudienceInput
  plan: PublishPlan
  wording: PublishWording
  /** Who can use it once everything is approved, as the line under "Who can use it". */
  audiencePhrase: string
  statements: { now: string; ifApproved: string | null }
  /** "What they get", with the coverage gaps. */
  consent: string
  /**
   * The values `consent` was rendered from, persisted as the replayable half of the record.
   *
   * Passed in rather than rebuilt here: the builder already holds the resolved names, counts and
   * coverage, and a second assembly of the same shape is a second thing to keep in step.
   */
  ackParams: AckPayloads['ack.publish']
  /** Who can use it right away, for the outcome ("Finance EMEA (12 people) can use it now."). */
  who: string
  /** Whether "only its maintainers" is the reader alone. */
  personal: boolean
  /** A reason it cannot proceed (blocking lint, no groups, unsaved), already in maker words. */
  blocked: string | null
  /**
   * Coverage for this choice is still being read. "What they get" names the bases that will not
   * reach some of them, and a sentence agreed to while it was being corrected would be a record of
   * something the maker did not read — so confirming waits for it.
   */
  measuring: boolean
  onClose: () => void
  onDone: (outcome: PublishOutcome) => void
}) => {
  // "Legal and Ops" in the outcome is `Intl.ListFormat`'s, so the dialog hands the wording
  // function the active locale rather than letting a pure module assume one — and the words
  // travel the same way, since a module-scope capture would freeze the language (PRD-27 F-3).
  const { formatList, locale, t } = useI18n()
  const strings = t.marketplace.publish
  const titleId = useId()
  const [ack, setAck] = useState(false)
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null)
  const publish = usePublishMakerAgent(agent.agentId)
  const restore = useRestoreMakerAgent(agent.agentId)
  const mutation = mode === 'publish' ? publish : restore

  const ackText = [statements.now, statements.ifApproved, consent]
    .filter((line): line is string => Boolean(line))
    .join(' ')
    .slice(0, ACK_MAX)

  const submit = () => {
    if (!ack || blocked !== null || measuring) return
    mutation.mutate(
      {
        audience,
        ack: true,
        ackText,
        ackKey: ACKS.publish.key,
        ackVersion: ACKS.publish.version,
        ackLocale: locale,
        ackParams
      },
      {
        onSuccess: (result) => {
          setOutcome(result)
          onDone(result)
        }
      }
    )
  }

  const done =
    outcome === null
      ? null
      : publishOutcomeWording(strings, { formatList }, outcome, { plan, action: mode, who, personal })
  const nameTaken = mutation.error instanceof ApiRequestError && mutation.error.status === 409

  return (
    <Dialog
      titleId={titleId}
      icon={<Users size={15} strokeWidth={2} />}
      title={done ? done.title : wording.title}
      lead={done ? undefined : wording.lead}
      onClose={onClose}
      width="lg"
    >
      {done === null ? (
        <>
          <DialogBody>
            <section>
              <h3 className="eyebrow mb-1.5">{strings.audienceHeading}</h3>
              {/* The phrase stands alone in its own element: the browser suite matches it
                  exactly, so nothing else may share this node. */}
              <p className="text-[14px]">{audiencePhrase}</p>
            </section>

            <section className="grid gap-3 sm:grid-cols-2">
              <div className={statements.ifApproved === null ? 'sm:col-span-2' : undefined}>
                <h3 className="eyebrow mb-1.5">{strings.nowHeading}</h3>
                <p className="text-pretty text-[13px] leading-[1.55]">{statements.now}</p>
              </div>
              {statements.ifApproved !== null && (
                <div>
                  <h3 className="eyebrow mb-1.5">{strings.ifApprovedHeading}</h3>
                  <p className="text-pretty text-[13px] leading-[1.55]">{statements.ifApproved}</p>
                </div>
              )}
            </section>

            <section>
              <h3 className="eyebrow mb-1.5 flex items-center gap-2">
                {wording.consentHeading}
                {measuring && <Spinner size={11} />}
              </h3>
              <p className="rounded-tile border border-rule bg-card px-3.5 py-3 text-pretty text-[13.5px] leading-[1.55]">
                {consent}
              </p>
            </section>

            {blocked && (
              <p className="flex items-start gap-2 rounded-tile border border-alert/40 bg-alert-soft px-3 py-2.5 text-pretty text-[12.5px] leading-[1.5]">
                <span className="mt-[2px] shrink-0 text-alert" aria-hidden>
                  <TriangleAlert size={13} strokeWidth={2.1} />
                </span>
                {blocked}
              </p>
            )}

            <label className="flex cursor-pointer items-start gap-2.5 rounded-tile border border-rule-strong bg-card px-3.5 py-3">
              <input
                type="checkbox"
                checked={ack}
                onChange={(event) => setAck(event.target.checked)}
                className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
              />
              <span className="min-w-0">
                <span className="block text-[13.5px] font-semibold tracking-[-0.014em]">{wording.ackLabel}</span>
                <span className="mt-1 block text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                  {strings.ackHint}
                </span>
              </span>
            </label>

            {mutation.isError && (
              <div role="alert" className="rounded-tile border border-alert/40 bg-alert-soft px-3 py-2.5">
                <p className="text-[13px] font-semibold tracking-[-0.014em]">
                  {nameTaken ? strings.nameTakenTitle : strings.failed}
                </p>
                <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                  {nameTaken ? strings.nameTakenBody : describeApiError(t, mutation.error)}
                </p>
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {strings.cancel}
            </Button>
            <Button
              variant="primary"
              icon={mutation.isPending ? <Spinner size={13} /> : undefined}
              disabled={!ack || blocked !== null || measuring || mutation.isPending}
              onClick={submit}
            >
              {mutation.isPending ? (plan.kind === 'review' ? strings.sending : strings.publishing) : wording.confirm}
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogBody>
            <div
              className={
                outcome?.request === null
                  ? 'flex items-start gap-2.5 rounded-tile border border-grounded/40 bg-grounded-soft px-3.5 py-3'
                  : 'flex items-start gap-2.5 rounded-tile border border-rule-strong bg-card px-3.5 py-3'
              }
            >
              <span
                className={outcome?.request === null ? 'mt-[2px] text-grounded' : 'mt-[2px] text-fg-muted'}
                aria-hidden
              >
                {outcome?.request === null ? (
                  <CircleCheck size={15} strokeWidth={2.1} />
                ) : (
                  <Clock size={15} strokeWidth={2.1} />
                )}
              </span>
              <div className="min-w-0">
                <p role="status" className="text-pretty text-[13.5px] leading-[1.55]">
                  {done.body}
                </p>
                {done.note && (
                  <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{done.note}</p>
                )}
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="primary" onClick={onClose}>
              {strings.done}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  )
}
